import React from 'react';
import PropTypes from 'prop-types';
import { BeakerIcon, BellIcon, BroadcastIcon, DownloadIcon, GraphIcon, SyncIcon, ToolsIcon, VersionsIcon } from '@primer/octicons-react';
import ReactLoading from 'react-loading';
import NavigationMenu from './NavigationMenu';
import { ActionInProgress } from './Navigation.functions';
import s from './Navigation.css';

/**
 * Composant des outils de navigation
 * Affiche les outils selon le type d'utilisateur
 * 
 * @param {Object} props - Props du composant
 * @param {Object} props.me - Utilisateur connecté
 * @param {string} props.menuOpened - Clé du menu actuellement ouvert
 * @param {Function} props.getIconTag - Fonction pour obtenir les icônes
 * @param {Function} props.handleMenu - Handler pour les clics sur le menu
 * @param {Function} props.t - Fonction de traduction
 * @param {Object} props.state - État du composant parent (pour les loaders)
 * @param {Array} props.ftpAccesses - Accès FTP enregistrés, une entrée de menu chacun
 * @param {Function} props.updateAlarmAsync - Fonction pour mettre à jour les alarmes
 * @param {Function} props.updateAutoMeterAsync - Fonction pour synchroniser les compteurs
 * @param {Function} props.updateAutoTableAsync - Fonction pour mettre à jour les tables automatiques
 * @param {Function} props.updateGeoLocation - Fonction pour mettre à jour la géolocalisation
 * @returns {JSX.Element} Composant NavigationTools
 */
const NavigationTools = ({ 
  emitEvent,
  me, 
  menuOpened, 
  getIconTag, 
  handleMenu, 
  t, 
  state, 
  ftpAccesses = [],
  updateAlarmAsync, 
  updateAutoMeterAsync, 
  updateAutoTableAsync,
  updateGeoLocation
}) => {
  const actionInProgress = ActionInProgress([state.loadUpdateAlarm, state.loadUpdateAutoMeter, state.loadUpdateAutoTable, state.loadUpdateLocate]);

  let options = [];
  
  if (me.type < 10) {
    // Une entrée par accès enregistré, et non trois entrées figées : un second serveur du
    // même type n'avait sinon aucune page atteignable, alors que ses fichiers étaient bien
    // importés (issue #336).
    ftpAccesses.forEach(access => {
      options.push({
        icon: getIconTag(VersionsIcon),
        label: t("navigation.tools.viewftp", { label: access.label || access.code }),
        link: `/ftpfiles/${access.code}`,
      })
    })
  }

  options.push({
    icon: getIconTag(GraphIcon), 
    label: t("navigation.tools.viewmeters"), 
    link: "/meters"
  })

  options.push({
    icon: getIconTag(BellIcon),
    label: t("navigation.tools.viewalarms"),
    link: "/alarms"
  })

  if (me.type < 20) {
    options.push({
      icon: getIconTag(BellIcon),
      label: t("navigation.tools.viewnegativeflow"),
      link: "/negativeflow"
    })
  }

  options.push({
    icon: getIconTag(DownloadIcon),
    label: t("navigation.tools.importmeter"),
    link: "/deviceimport"
  })

  if (me.type < 10) {
    options.push({
      icon: state.loadUpdateAutoMeter ? <ReactLoading type="spin" color="#41a8ba" className={s.inline_block} width={25} height={25}/> : getIconTag(DownloadIcon), 
      label: t("navigation.tools.syncautometer.label"), 
      fct: updateAutoMeterAsync
    })

    options.push({
      icon: state.loadUpdateLocate ? <ReactLoading type="spin" color="#41a8ba" className={s.inline_block} width={25} height={25}/> : getIconTag(SyncIcon), 
      label: t("navigation.tools.updategeolocation.label"), 
      fct: updateGeoLocation
    })

    options.push({
      icon: state.loadUpdateAlarm ? <ReactLoading type="spin" color="#41a8ba" className={s.inline_block} width={25} height={25}/> : getIconTag(SyncIcon), 
      label: t("navigation.tools.updatealarm.label"), 
      fct: updateAlarmAsync
    })

    options.push({
      icon: state.loadUpdateAutoTable ? <ReactLoading type="spin" color="#41a8ba" className={s.inline_block} width={25} height={25}/> : getIconTag(SyncIcon), 
      label: t("navigation.tools.updateautotable.label"), 
      fct: updateAutoTableAsync
    })

    options.push({
      icon: getIconTag(BeakerIcon),
      label: t("navigation.tools.testframe"),
      link: "/tools"
    })

    options.push({
      icon: getIconTag(DownloadIcon),
      label: t("navigation.tools.extractreadings"),
      link: "/tools/extract-readings"
    })
  }

  if (me.type < 20) {
    options.splice(options.length - (me.type < 10 ? 1 : 0), 0, {
      icon: getIconTag(BeakerIcon),
      label: t("navigation.tools.tickets"),
      link: "/tickets"
    })
    options.push({
      icon: getIconTag(DownloadIcon),
      label: t("navigation.tools.ediimport"),
      link: "/ediimport"
    })
  }


  return (
    <NavigationMenu
      menuKey="tools"
      menuOpened={menuOpened}
      title={{ 
        icon: actionInProgress ? <ReactLoading type="spin" color="#41a8ba" className={s.inline_block} width={25} height={25}/> : getIconTag(ToolsIcon), 
        label: t("navigation.tools.label") 
      }}
      options={options}
      handleMenu={handleMenu}
    />
  );
};

NavigationTools.propTypes = {
  me: PropTypes.object,
  menuOpened: PropTypes.string,
  getIconTag: PropTypes.func.isRequired,
  handleMenu: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  state: PropTypes.object.isRequired,
  ftpAccesses: PropTypes.array,
  updateAlarmAsync: PropTypes.func.isRequired,
  updateAutoMeterAsync: PropTypes.func.isRequired,
  updateAutoTableAsync: PropTypes.func.isRequired
};

export default NavigationTools;
